import { useContext, useEffect, useMemo } from 'react';
import { MobXProviderContext } from 'mobx-react';
import { Measurement } from '@kentik/ui-shared/query/query.types';
import { getMeasurementSync } from '@kentik/ui-shared/query/measurements';
import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { AppProtocolsDictionary } from '@kentik/ui-shared/dictionary/types';
import type { LookupStore } from 'app/stores/$lookups';
import { api } from 'core/util';
import { MeasurementListResponse } from '@kentik/ui-shared/dictionary/v2/api';
import { filterRecentMeasurements } from '@kentik/ui-shared/dictionary/v2/compilers/transformAndFilterReconMeasurements';
import { queryClient } from 'app/queryClient';

export interface DictionaryMeasurementsState {
  isLoading: boolean;
  isError: boolean;
  isSuccess: boolean;
}

export type MeasurementHashEntry = Pick<Measurement, 'displayName' | 'storage'>;
export type MeasurementHash = Record<string, MeasurementHashEntry>;

type DictionaryMeasurementsResult = UseQueryResult<MeasurementListResponse> & {
  measurements: Measurement[];
  measurementHash: MeasurementHash;
  appProtocols: AppProtocolsDictionary | undefined;
};

const QUERY_KEY = ['dictionary/measurements'];
const STALE_TIME = 300000;

function fetchDictionaryMeasurements(): Promise<MeasurementListResponse> {
  return api.get('/api/ui/dictionary/measurements');
}

export async function getMeasurement(measurementName: string): Promise<Measurement | undefined> {
  let data: MeasurementListResponse | undefined;
  try {
    data = await queryClient.ensureQueryData({
      queryKey: QUERY_KEY,
      queryFn: fetchDictionaryMeasurements,
      staleTime: STALE_TIME
    });
  } catch (_e) {
    // ignore error, will return undefined
  }

  const measurements = filterRecentMeasurements(data?.measurements ?? [], 90 * 24 * 3600);
  return measurements.find((measurement) => measurement.measurement === measurementName);
}

/**
 * Fetches dictionary measurements
 */
export const useDictionaryMeasurements = (): DictionaryMeasurementsResult => {
  const { $lookups } = useContext(MobXProviderContext) as { $lookups?: LookupStore };

  const result = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchDictionaryMeasurements,
    staleTime: STALE_TIME
  });

  const appProtocols = result.data?.appProtocols;

  useEffect(() => {
    if (result.data?.assetTags && $lookups) {
      $lookups.registerAssetTagHandlers(result.data.assetTags);
    }
  }, [result.data, $lookups]);

  const measurements = useMemo(() => {
    const m = result.data?.measurements || [];
    return filterRecentMeasurements(m, 90 * 24 * 3600);
  }, [result.data]);

  /*
   * TODO: I'm propagating this through in order to make it easier to grab things by measurement name. However my actual
   * use case was only partially solved by this and I still have to iterate over keys so I'm not convinced that this is
   *  necessary and all that code related to measurementHash could be removed as long as measurementTransforms.ts is
   *  updated to deal with Measurement[].
   */
  const measurementHash = useMemo(
    () =>
      measurements.reduce<MeasurementHash>((acc, { measurement, displayName, storage }) => {
        acc[measurement] = { displayName, storage };
        return acc;
      }, {}),
    [measurements]
  );

  return {
    ...result,
    measurements,
    measurementHash,
    appProtocols
  };
};

interface DictionaryMeasurementResult extends DictionaryMeasurementsState {
  measurement: Measurement | null;
}

export const useDictionaryMeasurement = (measurementName: string | null): DictionaryMeasurementResult => {
  const { measurements: allMeasurements = [], ...state } = useDictionaryMeasurements();
  return { measurement: measurementName ? getMeasurementSync(measurementName, allMeasurements) : null, ...state };
};
